import axios from 'axios';
import Config from '../config';
import apiClient from './apiClient';

const TEST_AUTOMATION_HEADER_NAME = 'X-Test-Automation';
const TEST_AUTOMATION_HEADER_VALUE = 'true';

let isInstalled = false;

const isTruthy = (value?: string): boolean => {
  if (!value) {
    return false;
  }

  const normalized = value.trim().toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'yes';
};

const isSupportedLocalHost = (hostname: string): boolean =>
  hostname === 'localhost' || hostname === '127.0.0.1';

const hasHostedAutomationQueryFlag = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const hostname = window.location.hostname.toLowerCase();
  if (hostname !== 'assetron.dev' && !hostname.endsWith('.assetron.dev')) {
    return false;
  }

  return isTruthy(
    new URLSearchParams(window.location.search).get('testAutomation') ??
      undefined,
  );
};

const shouldAttachHeader = (url: string): boolean => {
  try {
    const parsedUrl = new URL(url, globalThis.location.origin);
    const hostname = parsedUrl.hostname.toLowerCase();

    return (
      isSupportedLocalHost(hostname) ||
      hostname === 'assetron.dev' ||
      hostname.endsWith('.assetron.dev')
    );
  } catch {
    return false;
  }
};

const shouldEnableTestAutomationHeaders = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  if (hasHostedAutomationQueryFlag()) {
    return true;
  }

  if (!isSupportedLocalHost(window.location.hostname.toLowerCase())) {
    return false;
  }

  return isTruthy(Config.ENABLE_TEST_AUTOMATION_HEADER);
};

const installAxiosDefaults = (): void => {
  axios.defaults.headers.common[TEST_AUTOMATION_HEADER_NAME] =
    TEST_AUTOMATION_HEADER_VALUE;
  apiClient.defaults.headers.common[TEST_AUTOMATION_HEADER_NAME] =
    TEST_AUTOMATION_HEADER_VALUE;
};

const installFetchInterceptor = (): void => {
  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init) => {
    const requestUrl =
      typeof input === 'string' || input instanceof URL
        ? input.toString()
        : input.url;

    if (!shouldAttachHeader(requestUrl)) {
      return originalFetch(input, init);
    }

    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined),
    );
    headers.set(TEST_AUTOMATION_HEADER_NAME, TEST_AUTOMATION_HEADER_VALUE);

    return originalFetch(input, {
      ...init,
      headers,
    });
  };
};

const installXmlHttpRequestInterceptor = (): void => {
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;
  const originalSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader;

  XMLHttpRequest.prototype.open = function open(method, url, ...rest) {
    Reflect.set(this, '__assetronTestAutomationUrl', String(url));
    return originalOpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function send(body) {
    const requestUrl = Reflect.get(this, '__assetronTestAutomationUrl');

    if (typeof requestUrl === 'string' && shouldAttachHeader(requestUrl)) {
      try {
        originalSetRequestHeader.call(
          this,
          TEST_AUTOMATION_HEADER_NAME,
          TEST_AUTOMATION_HEADER_VALUE,
        );
      } catch {
        // Ignore duplicate/invalid header writes on reused XHR objects.
      }
    }

    return originalSend.call(this, body);
  };
};

export function installTestAutomationHeaders(): void {
  if (isInstalled || !shouldEnableTestAutomationHeaders()) {
    return;
  }

  installAxiosDefaults();
  installFetchInterceptor();
  installXmlHttpRequestInterceptor();
  isInstalled = true;
}
